// NEW_FILE_JUSTIFIED: Document upload dialog for KB — drag-drop file upload with validation, no existing equivalent
import { useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Upload, X, FileText, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDocumentUploadForm } from '@/hooks/useDocumentUploadForm';
import type { IDocumentUploadDialogProps } from '@/@types/knowledgeBase';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentUploadDialog({ open, onOpenChange, onUpload }: IDocumentUploadDialogProps) {
  const {
    files,
    isShared,
    isDragOver,
    error,
    isSubmitting,
    fileInputRef,
    addFiles,
    removeFile,
    setIsDragOver,
    setIsShared,
    setIsSubmitting,
    reset,
  } = useDocumentUploadForm();

  const handleClose = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) reset();
      onOpenChange(nextOpen);
    },
    [onOpenChange, reset]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles, setIsDragOver]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(true);
    },
    [setIsDragOver]
  );

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, [setIsDragOver]);

  const handleSubmit = useCallback(async () => {
    if (files.length === 0 || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onUpload({ files, isSharedWithCompanies: isShared });
      handleClose(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [files, isShared, isSubmitting, onUpload, handleClose, setIsSubmitting]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" aria-hidden="true" />
            Upload Documents
          </DialogTitle>
          <DialogDescription>
            Upload files to your knowledge base. Accepted formats: PDF, DOCX, TXT, MD, PNG, JPG,
            JPEG, WEBP. Max 50MB per file.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.webp"
            multiple
            onChange={e => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = '';
            }}
          />

          <div
            role="button"
            tabIndex={0}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={e => e.key === 'Enter' && fileInputRef.current?.click()}
            className={cn(
              'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed',
              'bg-card py-8 px-6 text-center cursor-pointer transition-colors',
              isDragOver ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'
            )}
          >
            <Upload
              className={cn('w-8 h-8', isDragOver ? 'text-primary' : 'text-muted-foreground')}
              aria-hidden="true"
            />
            <div>
              <p className="text-sm font-medium text-foreground">
                {isDragOver ? 'Drop files here' : 'Click or drag files to upload'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PDF, DOCX, TXT, MD, PNG, JPG, JPEG, WEBP — up to 50MB each
              </p>
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2.5">
              <AlertCircle
                className="w-4 h-4 text-destructive shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {files.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-foreground">Selected files ({files.length})</p>
              {files.map((file, index) => (
                <div
                  key={`${file.name}-${file.size}`}
                  className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-muted/50"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText
                      className="w-4 h-4 text-muted-foreground shrink-0"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-foreground truncate">{file.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {formatFileSize(file.size)}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 shrink-0"
                    onClick={e => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    aria-label={`Remove ${file.name}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Checkbox
              id="upload-shared"
              checked={isShared}
              onCheckedChange={checked => setIsShared(checked === true)}
            />
            <Label htmlFor="upload-shared" className="text-sm font-normal cursor-pointer">
              Share with all companies
            </Label>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => handleClose(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleSubmit()}
              disabled={files.length === 0 || isSubmitting}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
              {isSubmitting
                ? 'Uploading...'
                : `Upload ${files.length > 0 ? `(${files.length})` : ''}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
